import indexedDBService, { PendingOperation } from './indexedDB';
import { getAllTasks, PaginatedResponse } from './tasks';
import { getShippings, getUserShippings } from './api';
import { getToken, getCurrentUser } from './auth';
import { Task } from '../types/task';
import { Shipping } from '../types/shipping';

// Helper function to extract Task[] from getAllTasks result
const extractTasksArray = (result: Task[] | PaginatedResponse<Task>): Task[] => {
  return Array.isArray(result) ? result : result.tasks;
};

interface SyncOptions {
  force?: boolean;
  background?: boolean;
  onProgress?: (progress: { completed: number; total: number; message: string }) => void;
  onError?: (error: Error) => void;
}

interface SyncResult {
  success: boolean;
  synced: {
    tasks: number;
    shipments: number;
    quotes: number;
    contacts: number;
  };
  errors: string[];
  duration: number;
}

class BackgroundSyncService {
  private syncInProgress = false;
  private lastFullSync = 0;
  private syncInterval: NodeJS.Timeout | null = null;
  private readonly FULL_SYNC_INTERVAL = 10 * 60 * 1000; // 10 minutes (increased to reduce server load)
  private readonly INCREMENTAL_SYNC_INTERVAL = 60 * 1000; // 60 seconds (increased to reduce API calls)
  private readonly MAX_RETRIES = 3;
  private isEnabled = false; // Disabled by default - TaskDashboard now handles its own data loading

  constructor() {
    // Don't start periodic sync automatically - let components opt-in
    this.setupOnlineListener();
  }

  private setupOnlineListener(): void {
    window.addEventListener('online', () => {
      console.log('Connection restored');
      // Only sync if enabled and there are pending operations
      if (this.isEnabled) {
        this.syncData({ background: true });
      }
    });

    window.addEventListener('offline', () => {
      console.log('Connection lost, enabling offline mode...');
    });
  }

  // Enable periodic sync (opt-in for components that need it)
  enablePeriodicSync(): void {
    if (this.isEnabled) return;
    this.isEnabled = true;
    this.startPeriodicSync();
    console.log('Background sync enabled');
  }

  // Disable periodic sync
  disablePeriodicSync(): void {
    this.isEnabled = false;
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    console.log('Background sync disabled');
  }

  private startPeriodicSync(): void {
    // Clear any existing interval
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    // Only set up periodic sync if enabled
    if (!this.isEnabled) return;

    // Set up periodic sync with longer interval to reduce API load
    this.syncInterval = setInterval(() => {
      if (navigator.onLine && !this.syncInProgress && this.isEnabled) {
        this.syncData({ background: true });
      }
    }, this.INCREMENTAL_SYNC_INTERVAL);
  }

  async syncData(options: SyncOptions = {}): Promise<SyncResult> {
    if (this.syncInProgress && !options.force) {
      console.log('Sync already in progress, skipping...');
      return {
        success: false,
        synced: { tasks: 0, shipments: 0, quotes: 0, contacts: 0 },
        errors: ['Sync already in progress'],
        duration: 0
      };
    }

    const startTime = Date.now();
    this.syncInProgress = true;

    const result: SyncResult = {
      success: true,
      synced: { tasks: 0, shipments: 0, quotes: 0, contacts: 0 },
      errors: [],
      duration: 0
    };

    try {
      // First, process pending operations if online
      if (navigator.onLine) {
        await this.processPendingOperations(options);
      }

      // Check if full sync is needed
      const needsFullSync = options.force || 
        (Date.now() - this.lastFullSync) > this.FULL_SYNC_INTERVAL ||
        await this.shouldPerformFullSync();

      if (needsFullSync) {
        await this.performFullSync(result, options);
        this.lastFullSync = Date.now();
      } else {
        await this.performIncrementalSync(result, options);
      }

      // Update sync status in IndexedDB
      await indexedDBService.updateSyncStatus({
        lastSync: Date.now(),
        pendingOperations: []
      });

      result.success = result.errors.length === 0;
      result.duration = Date.now() - startTime;

      if (options.background && result.success) {
        console.log(`Background sync completed: ${result.synced.tasks} tasks, ${result.synced.shipments} shipments, ${result.synced.contacts} contacts in ${result.duration}ms`);
      }

    } catch (error) {
      console.error('Sync failed:', error);
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Unknown sync error');
      
      if (options.onError) {
        options.onError(error instanceof Error ? error : new Error('Unknown sync error'));
      }
    } finally {
      this.syncInProgress = false;
    }

    return result;
  }

  private async shouldPerformFullSync(): Promise<boolean> {
    // Check if cache is expired
    const tasksExpired = await indexedDBService.isExpired('tasks');
    const shipmentsExpired = await indexedDBService.isExpired('shipments');
    
    return tasksExpired || shipmentsExpired;
  }

  private async performFullSync(result: SyncResult, options: SyncOptions): Promise<void> {
    const user = await getCurrentUser();
    if (!user) throw new Error('No authenticated user');

    let progress = 0;
    const totalSteps = 2; // shipments, quotes (tasks handled by TaskDashboard directly)

    try {
      // OPTIMIZATION: Tasks are now loaded on-demand by TaskDashboard with server-side pagination
      // Background sync no longer syncs tasks to reduce API load and improve performance
      options.onProgress?.({ completed: progress++, total: totalSteps, message: 'Background sync active...' });
      result.synced.tasks = 0; // Tasks handled by components directly

      // Sync shipments - lightweight sync for offline caching only
      options.onProgress?.({ completed: progress++, total: totalSteps, message: 'Syncing shipments...' });
      
      // Only sync a small batch of shipments for offline access
      // Full data is loaded by TaskDashboard with proper pagination
      try {
        const token = getToken();
        if (token) {
          const response = await fetch('/.netlify/functions/shippings?page=1&limit=20', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            const shipments = data.shipments || data || [];
            if (shipments.length > 0) {
              await indexedDBService.putShipments(shipments);
              result.synced.shipments = shipments.length;
            }
          }
        }
      } catch (error) {
        console.log('Shipments sync skipped:', error);
        result.synced.shipments = 0;
      }

      // Sync quotes and contacts (admin only) - keep for offline reference
      if (user.role === 'admin') {
        try {
          const quotes = await this.fetchQuotes();
          if (quotes.length > 0) {
            await indexedDBService.putQuotes(quotes);
            result.synced.quotes = quotes.length;
          }
        } catch (error) {
          console.log('Quotes sync skipped:', error);
        }

        // Sync contacts for admin users
        try {
          const contacts = await this.fetchContacts();
          if (contacts.length > 0) {
            await indexedDBService.putContacts(contacts);
            result.synced.contacts = contacts.length;
          }
        } catch (error) {
          console.log('Contacts sync skipped:', error);
        }
      }

    } catch (error) {
      console.error('Full sync error:', error);
      throw error;
    }
  }

  private async performIncrementalSync(result: SyncResult, options: SyncOptions): Promise<void> {
    const user = await getCurrentUser();
    if (!user) return;

    // Get last sync time
    const syncStatus = await indexedDBService.getSyncStatus();
    const lastSync = new Date(syncStatus.lastSync);

    try {
      // For incremental sync, we could implement change tracking on the server
      // For now, we'll do a lightweight check of recent updates
      
      // Get recent task updates
      const recentTasks = await this.getRecentTasks(lastSync);
      if (recentTasks.length > 0) {
        await indexedDBService.putTasks(recentTasks);
        result.synced.tasks = recentTasks.length;
      }

      // Get recent shipment updates
      const recentShipments = await this.getRecentShipments(lastSync);
      if (recentShipments.length > 0) {
        await indexedDBService.putShipments(recentShipments);
        result.synced.shipments = recentShipments.length;
      }

    } catch (error) {
      console.error('Incremental sync error:', error);
      // Fall back to full sync on incremental sync failure
      await this.performFullSync(result, options);
    }
  }

  private async getWorkerTasks(workerId: string): Promise<Task[]> {
    try {
      // The server returns the tasks on the shipments this person may see, which is
      // the same rule the shipments themselves follow. Filtering again here to "only
      // the ones assigned to me" hid the rest of their work.
      const result = await getAllTasks();
      return extractTasksArray(result);
    } catch (error) {
      console.error('Error fetching worker tasks:', error);
      return [];
    }
  }

  private async getWorkerShipments(workerId: string): Promise<Shipping[]> {
    // Which shipments a member of staff may see is the server's decision -- every
    // shipment their sections allow, minus anything hidden from them. This used to work
    // it out again here, from their tasks, which left a new starter with an empty
    // dashboard even after the server had stopped narrowing.
    try {
      const allShipments = await getUserShippings(getToken()!);
      return Array.isArray(allShipments) ? allShipments : allShipments.shipments || [];
    } catch (error) {
      console.error('Error fetching worker shipments:', error);
      return [];
    }
  }

  private async getRecentTasks(since: Date): Promise<Task[]> {
    try {
      // Fetch all tasks and filter by update date on the client side
      // This is a reasonable approach until server-side filtering is implemented
      const user = await getCurrentUser();
      if (!user) return [];

      const result = await getAllTasks();
      const allTasks = extractTasksArray(result);
      const sinceTime = since.getTime();
      
      // Filter tasks that have been updated since the last sync
      return allTasks.filter((task: Task) => {
        // Handle both Date and MongoDB { $date: string } formats
        const rawDate = task.updatedAt as Date | { $date: string } | string | undefined;
        let updatedTime = 0;
        
        if (rawDate) {
          if (typeof rawDate === 'object' && '$date' in rawDate) {
            updatedTime = new Date(rawDate.$date).getTime();
          } else if (rawDate instanceof Date) {
            updatedTime = rawDate.getTime();
          } else if (typeof rawDate === 'string') {
            updatedTime = new Date(rawDate).getTime();
          }
        }
        
        return updatedTime > sinceTime;
      });
    } catch (error) {
      console.error('Error fetching recent tasks:', error);
      return [];
    }
  }

  private async getRecentShipments(since: Date): Promise<Shipping[]> {
    try {
      // Fetch all shipments and filter by ETD date on the client side
      // Shipping type doesn't have updatedAt, so we use ETD to prioritize recent shipments
      const token = getToken();
      if (!token) return [];

      const response = await getShippings(token);
      
      // Handle paginated response - API returns { shipments: [...], pagination: {...} }
      const allShipments: Shipping[] = Array.isArray(response) 
        ? response 
        : (response?.shipments || []);
      
      const sinceTime = since.getTime();
      
      // Filter shipments based on ETD - include shipments with recent ETD dates
      // This helps keep actively shipping items in sync
      return allShipments.filter((shipment: Shipping) => {
        // Parse ETD date if available
        const etdDate = shipment.etd ? new Date(shipment.etd).getTime() : 0;
        // Include shipments with ETD within the sync window or in the future
        return etdDate > sinceTime || etdDate > Date.now() - (30 * 24 * 60 * 60 * 1000); // Last 30 days
      });
    } catch (error) {
      console.error('Error fetching recent shipments:', error);
      return [];
    }
  }

  private async fetchQuotes(): Promise<any[]> {
    try {
      const token = getToken();
      if (!token) return [];

      const response = await fetch('/.netlify/functions/quotes?includeContacts=true', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch quotes: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error fetching quotes:', error);
      return [];
    }
  }

  private async fetchContacts(): Promise<any[]> {
    try {
      const token = getToken();
      if (!token) return [];

      const response = await fetch('/.netlify/functions/quotes?operation=contacts', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch contacts: ${response.status}`);
      }

      const data = await response.json();
      // Handle both array and paginated response formats
      return Array.isArray(data) ? data : (data.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      return [];
    }
  }

  private async processPendingOperations(options: SyncOptions): Promise<void> {
    const pendingOps = await indexedDBService.getPendingOperations();
    const totalOps = pendingOps.length;
    let completed = 0;
    
    // Report initial progress if callback provided
    if (options.onProgress && totalOps > 0) {
      options.onProgress({
        completed: 0,
        total: totalOps,
        message: `Processing ${totalOps} pending operations...`
      });
    }
    
    for (const op of pendingOps) {
      try {
        await this.executePendingOperation(op);
        await indexedDBService.removePendingOperation(op.id);
        completed++;
        
        // Report progress after each successful operation
        if (options.onProgress) {
          options.onProgress({
            completed,
            total: totalOps,
            message: `Processed ${completed}/${totalOps} pending operations`
          });
        }
      } catch (error) {
        console.error(`Failed to execute pending operation ${op.id}:`, error);
        
        // Report error through callback if provided
        if (options.onError) {
          options.onError(error instanceof Error ? error : new Error(`Failed operation ${op.id}`));
        }
        
        // Increment retry count
        op.retries++;
        if (op.retries >= this.MAX_RETRIES) {
          console.error(`Max retries reached for operation ${op.id}, removing from queue`);
          await indexedDBService.removePendingOperation(op.id);
        } else {
          // Update the operation with incremented retry count
          await indexedDBService.put('pending_operations', op);
        }
      }
    }
  }

  private async executePendingOperation(op: PendingOperation): Promise<void> {
    switch (op.type) {
      case 'create':
        await this.executeCreateOperation(op);
        break;
      case 'update':
        await this.executeUpdateOperation(op);
        break;
      case 'delete':
        await this.executeDeleteOperation(op);
        break;
      default:
        throw new Error(`Unknown operation type: ${op.type}`);
    }
  }

  private async executeCreateOperation(op: PendingOperation): Promise<void> {
    const token = getToken();
    if (!token) throw new Error('No authentication token');
    
    console.log(`Executing create operation for ${op.table}:`, op.data);
    
    // Route to appropriate endpoint based on table
    let endpoint = '';
    switch (op.table) {
      case 'tasks':
        endpoint = '/.netlify/functions/tasks';
        break;
      case 'shipments':
      case 'shippings':
        endpoint = '/.netlify/functions/shipping';
        break;
      case 'quotes':
        endpoint = '/.netlify/functions/quotes';
        break;
      case 'shipping_updates':
        endpoint = `/.netlify/functions/shipping-update/${op.data.shipmentId}`;
        break;
      default:
        throw new Error(`Unsupported table for create: ${op.table}`);
    }
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(op.data)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to create ${op.table}: ${response.status}`);
    }
  }

  private async executeUpdateOperation(op: PendingOperation): Promise<void> {
    const token = getToken();
    if (!token) throw new Error('No authentication token');
    
    console.log(`Executing update operation for ${op.table}:`, op.data);
    
    // Route to appropriate endpoint based on table
    let endpoint = '';
    const id = op.data._id?.$oid || op.data._id || op.data.id;
    
    switch (op.table) {
      case 'tasks':
        endpoint = `/.netlify/functions/tasks?taskId=${id}`;
        break;
      case 'shipments':
      case 'shippings':
        endpoint = `/.netlify/functions/shipping/${id}`;
        break;
      case 'quotes':
        endpoint = `/.netlify/functions/quotes/${id}`;
        break;
      case 'shipping_updates':
        endpoint = `/.netlify/functions/shipping-update/${id}`;
        break;
      default:
        throw new Error(`Unsupported table for update: ${op.table}`);
    }
    
    const response = await fetch(endpoint, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(op.data)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to update ${op.table}: ${response.status}`);
    }
  }

  private async executeDeleteOperation(op: PendingOperation): Promise<void> {
    const token = getToken();
    if (!token) throw new Error('No authentication token');
    
    console.log(`Executing delete operation for ${op.table}:`, op.data);
    
    // Route to appropriate endpoint based on table
    let endpoint = '';
    const id = op.data._id?.$oid || op.data._id || op.data.id;
    
    switch (op.table) {
      case 'tasks':
        endpoint = `/.netlify/functions/tasks?taskId=${id}`;
        break;
      case 'shipments':
      case 'shippings':
        endpoint = `/.netlify/functions/shipping/${id}`;
        break;
      case 'quotes':
        endpoint = `/.netlify/functions/quotes/${id}`;
        break;
      case 'shipping_updates':
        endpoint = `/.netlify/functions/shipping-update/${id}`;
        break;
      default:
        throw new Error(`Unsupported table for delete: ${op.table}`);
    }
    
    const response = await fetch(endpoint, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete ${op.table}: ${response.status}`);
    }
  }

  // Public methods for manual sync control
  async forceSync(onProgress?: SyncOptions['onProgress']): Promise<SyncResult> {
    return this.syncData({ force: true, onProgress });
  }

  async syncTasks(): Promise<void> {
    const user = await getCurrentUser();
    if (!user) return;

    let tasks: Task[] = [];
    if (user.role === 'admin') {
      const result = await getAllTasks();
      tasks = extractTasksArray(result);
    } else if (user.role === 'worker') {
      tasks = await this.getWorkerTasks(user._id.toString());
    }

    if (tasks.length > 0) {
      await indexedDBService.putTasks(tasks);
    }
  }

  async syncShipments(): Promise<void> {
    const user = await getCurrentUser();
    if (!user) return;

    let shipments: Shipping[] = [];
    if (user.role === 'admin') {
      shipments = await getShippings(getToken()!);
    } else if (user.role === 'worker') {
      shipments = await this.getWorkerShipments(user._id.toString());
    } else {
      shipments = await getUserShippings(getToken()!);
    }

    if (shipments.length > 0) {
      await indexedDBService.putShipments(shipments);
    }
  }

  async syncContacts(): Promise<void> {
    const user = await getCurrentUser();
    // Only admins can sync contacts
    if (!user || user.role !== 'admin') return;

    const contacts = await this.fetchContacts();
    if (contacts.length > 0) {
      await indexedDBService.putContacts(contacts);
    }
  }

  // Offline operation queueing
  async queueOperation(
    type: 'create' | 'update' | 'delete',
    table: string,
    data: any
  ): Promise<void> {
    await indexedDBService.addPendingOperation({ type, table, data });
    
    // Try to sync immediately if online
    if (navigator.onLine && !this.syncInProgress) {
      this.syncData({ background: true });
    }
  }

  // Smart cache management
  async getCachedTasks(filters?: any): Promise<Task[]> {
    const user = await getCurrentUser();
    if (!user) return [];

    // Check if cache is fresh
    const isExpired = await indexedDBService.isExpired('tasks');
    if (isExpired && navigator.onLine) {
      // Refresh cache in background
      this.syncTasks();
    }

    // Apply user-specific filtering
    if (user.role === 'worker') {
      return indexedDBService.getTasksAssignedToWorker(user._id.toString());
    } else if (filters) {
      return indexedDBService.queryTasks(filters);
    } else {
      return indexedDBService.getTasks();
    }
  }

  async getCachedShipments(filters?: any): Promise<Shipping[]> {
    const user = await getCurrentUser();
    if (!user) return [];

    // Check if cache is fresh
    const isExpired = await indexedDBService.isExpired('shipments');
    if (isExpired && navigator.onLine) {
      // Refresh cache in background
      this.syncShipments();
    }

    if (user.role === 'worker') {
      // The stored copy holds exactly what the server sent this person, so it is read
      // back as it is. Narrowing it again here by tasks is what left a new starter
      // looking at an empty dashboard.
      return indexedDBService.getShipments();
    } else if (filters?.clientEmail) {
      return indexedDBService.getShipmentsByClient(filters.clientEmail);
    } else {
      return indexedDBService.getShipments();
    }
  }

  // Cleanup methods
  stop(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  async clearCache(): Promise<void> {
    await indexedDBService.clearAllData();
  }

  // Health check
  async getStatus(): Promise<{
    isOnline: boolean;
    syncInProgress: boolean;
    lastSync: number;
    pendingOperations: number;
    cacheSize: {
      tasks: number;
      shipments: number;
      quotes: number;
      contacts: number;
    };
  }> {
    const syncStatus = await indexedDBService.getSyncStatus();
    const pendingOps = await indexedDBService.getPendingOperations();
    const tasks = await indexedDBService.getTasks();
    const shipments = await indexedDBService.getShipments();
    const quotes = await indexedDBService.getQuotes();
    const contacts = await indexedDBService.getContacts();

    return {
      isOnline: navigator.onLine,
      syncInProgress: this.syncInProgress,
      lastSync: syncStatus.lastSync,
      pendingOperations: pendingOps.length,
      cacheSize: {
        tasks: tasks.length,
        shipments: shipments.length,
        quotes: quotes.length,
        contacts: contacts.length
      }
    };
  }
}

// Singleton instance
const backgroundSyncService = new BackgroundSyncService();

export default backgroundSyncService;
export { BackgroundSyncService };
export type { SyncOptions, SyncResult };
